import { useMemo } from "react"
import { useDebounce } from "use-debounce"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { ConfigQuery, fetchFreeBusyScheduleForResources } from "../../../createOrderApi"
import { ResourceFiltersTypes } from "../../../createOrderTypes"
import { NullableCalendarEvent, TimelineCalendarResource } from "../calendar/fullCalendarTypes"
import { filterResources } from "../helpers"
import { ViewType } from "../resourceBookingCalendars/BookingViewMenu"

import {
    AVAILABILITY_VIEW_INTERVAL_MINUTES,
    MAX_AVAILABILITY_WINDOW_HOURS,
    MEETING_WINDOW_DEBOUNCE_MS,
    selectBusyResourceKeys,
    SITTINGS_FILTER_IDENTIFIER,
} from "./meetingFit"

export type MeetingWindow = Readonly<{
    start: string
    end: string
}>

type UseMeetingAvailabilityArgs = Readonly<{
    userId: number
    isOutlookFlow: boolean
    calendarResources: TimelineCalendarResource[]
    filters: ResourceFiltersTypes
    selectedView: ViewType
    draftEvent: NullableCalendarEvent
}>

type MeetingAvailability = Readonly<{
    meetingWindow: MeetingWindow | null
    /** Applied to the timeline: empty while the meeting narrowing is off. */
    busyResourceIds: ReadonlySet<string> | undefined
    /** Busy at the meeting time regardless of the narrowing, for the banner counts. */
    meetingBusyResourceIds: ReadonlySet<string> | undefined
    isAwaitingAvailability: boolean
}>

export function useMeetingAvailability({
    userId,
    isOutlookFlow,
    calendarResources,
    filters,
    selectedView,
    draftEvent,
}: UseMeetingAvailabilityArgs): MeetingAvailability {
    const dateTimeFactory = useDateTime()

    // Debounced as one string so dragging or resizing the draft event does not re-key the free/busy query
    // on every frame; `leading` keeps the seeded Outlook window applied on the very first render.
    const meetingWindowKey = draftEvent?.start && draftEvent?.end ? `${draftEvent.start}|${draftEvent.end}` : ""
    const [debouncedMeetingWindowKey] = useDebounce(meetingWindowKey, MEETING_WINDOW_DEBOUNCE_MS, { leading: true })

    const meetingWindow = useMemo<MeetingWindow | null>(() => {
        if (!debouncedMeetingWindowKey) return null

        const [start, end] = debouncedMeetingWindowKey.split("|")
        const startDateTime = dateTimeFactory.fromISO(start)
        const endDateTime = dateTimeFactory.fromISO(end)
        if (endDateTime <= startDateTime) return null
        if (endDateTime.diff(startDateTime, "hours").hours > MAX_AVAILABILITY_WINDOW_HOURS) return null

        return { start, end }
    }, [debouncedMeetingWindowKey, dateTimeFactory])

    // Timeline only: the availability view is where the user looks for another time, so hiding what is
    // busy at the meeting time there would hide exactly the alternatives they came for.
    const isMeetingWindowOnTimeline = isOutlookFlow && !!meetingWindow && selectedView === ViewType.TIMELINE
    const isAvailabilityFilterOn = isMeetingWindowOnTimeline && !!filters.onlyAvailableForMeeting

    // Ignores search, the active booking and the seats filter: none of them should re-key the free/busy
    // request, so flipping the banner between "fits" and "all" reuses the same response.
    const availabilityCandidateIdsKey = useMemo(
        () =>
            isMeetingWindowOnTimeline
                ? filterResources(
                      calendarResources,
                      filters.filters.filter((f) => f.identifier !== SITTINGS_FILTER_IDENTIFIER),
                      undefined,
                      null,
                  )
                      .map((resource) => resource.id)
                      .join(",")
                : "",
        [isMeetingWindowOnTimeline, calendarResources, filters.filters],
    )

    const { data: busyResourceKeys, isError: isAvailabilityError } = useApiQuery(fetchFreeBusyScheduleForResources, {
        queryName: ConfigQuery.RESOURCES_FREE_BUSY,
        dependencies: {
            userId,
            availabilityViewInterval: AVAILABILITY_VIEW_INTERVAL_MINUTES,
            startTime: meetingWindow?.start ?? "",
            endTime: meetingWindow?.end ?? "",
            resourceIds: availabilityCandidateIdsKey,
        },
        enabled: isMeetingWindowOnTimeline && !!availabilityCandidateIdsKey,
        // Fail open: a failed availability lookup must not hide resources.
        errorDisplay: ErrorDisplay.NONE,
        keepPreviousData: true,
        select: selectBusyResourceKeys,
    })

    // The free/busy call can only start once the resources are in, so without this the timeline would
    // paint every row and drop the busy ones a moment later. `isInitialLoading` is not enough: while the
    // query is still disabled it reads false, leaving a window in which the full list renders.
    const isAwaitingAvailability =
        isAvailabilityFilterOn && !!availabilityCandidateIdsKey && !busyResourceKeys && !isAvailabilityError

    const meetingBusyResourceIds = useMemo<ReadonlySet<string> | undefined>(() => {
        if (!isMeetingWindowOnTimeline || !busyResourceKeys?.size) return undefined

        const ids = new Set<string>()
        calendarResources.forEach((resource) => {
            if (busyResourceKeys.has(resource.id) || busyResourceKeys.has(resource.uid)) ids.add(resource.id)
        })

        return ids
    }, [isMeetingWindowOnTimeline, busyResourceKeys, calendarResources])

    const busyResourceIds = isAvailabilityFilterOn ? meetingBusyResourceIds : undefined

    return { meetingWindow, busyResourceIds, meetingBusyResourceIds, isAwaitingAvailability }
}
